import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { type BridgeData } from '@databases/database-state/state/bridge-data';
import { perf } from '@databases/logs/performance';

import {
  type ClientFeatureFlagName,
  currentFeatureFlagState,
  getFeatureFlagOverrides,
  setFeatureFlagOverride,
} from '@canvas/client-feature-flags';
import {
  applyExperimentParamOverrides,
  applyFeatureFlagOverridesToExperiments,
} from '@canvas/client-feature-flags/experiments';
import { getExperimentParamOverrides } from '@canvas/client-feature-flags/get-experiment-params-from-url';
import { initStatsig } from '@canvas/client-feature-flags/statsig';
import { setContext } from '@canvas/sentry-react';

const getFeatureFlagsInUse = (): string[] => {
  const featureFlagsInUse: string[] = [];

  for (const key in currentFeatureFlagState) {
    if (Object.hasOwn(currentFeatureFlagState, key)) {
      const flagKey = key as ClientFeatureFlagName;

      if (currentFeatureFlagState[flagKey].enabled && flagKey.startsWith('database')) {
        featureFlagsInUse.push(flagKey);
      }
    }
  }

  return featureFlagsInUse;
};

export const initializeFeatureFlags = async ({
  cloudId,
  userId,
}: Pick<BridgeData, 'cloudId' | 'userId'>) => {
  perf.timeLog('app-initialization', 'feature-flags-start');

  const featureFlagOverrides = getFeatureFlagOverrides(window.location.href);
  const experimentParamOverrides = getExperimentParamOverrides(window.location.href);

  setFeatureFlagOverride(featureFlagOverrides);
  applyFeatureFlagOverridesToExperiments(featureFlagOverrides);
  applyExperimentParamOverrides(experimentParamOverrides);
  await initStatsig(featureFlagOverrides, {
    accountId: userId,
    tenantId: cloudId,
  });

  const featureFlagsInUse = getFeatureFlagsInUse();

  setBooleanFeatureFlagResolver((flagKey) => {
    const alwaysOn = [
      'confluence-issue-terminology-refresh',
      'platform-component-visual-refresh',
      // Smart link feature flags
      // Needed to enable the smart link preview action size and hide the blanket
      // Required to remove smart card patch
      // See:
      // https://switcheroo.atlassian.com/ui/gates/f39f84c4-01ec-43a4-a0c1-3b6de5930ce1/key/linking_platform_hide_preview_action_blanket_prop
      // https://switcheroo.atlassian.com/ui/gates/2aed53b3-6766-4834-aafb-15ac38fe9586/key/platform_linking_enable_card_preview_action_size
      'platform_linking_enable_card_preview_action_size',
      'linking_platform_hide_preview_action_blanket_prop',
      // Required to remove linking common patch
      // https://switcheroo.atlassian.com/ui/gates/180b9a41-9d29-434b-bfef-e3ccce769b0a/key/platform_linking_update_linking_common_env_window
      'platform_linking_update_linking_common_env_window',
    ];

    return [...alwaysOn, ...featureFlagsInUse].includes(flagKey);
  });

  if (featureFlagsInUse.length > 0) {
    setContext('Feature Flags', {
      'In Use': featureFlagsInUse,
    });
  }
};
